import React, { useState, useEffect, useRef } from 'react';
import { translations } from '../translations';
import apiService from '../services/apiService';

const PROMIS33Questionnaire = ({ patientData, taskId, language = 'de', onComplete, onClose }) => {
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [activeSection, setActiveSection] = useState(0);
  const sectionRefs = useRef([]);
  const t = translations[language] || translations.en;

  // Fonction utilitaire pour créer un timestamp local sans décalage UTC
  const createLocalTimestamp = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;
  };

  // Charger les réponses existantes si elles existent
  useEffect(() => {
    const loadExistingResponses = async () => {
      if (!patientData || !patientData.patientId || !taskId) return;
      
      try {
        const response = await apiService.getQuestionnaireResponse(patientData.patientId, taskId);
        if (response.success && response.data) {
          const existingResponses = JSON.parse(response.data.responses);
          setResponses(existingResponses);
          setIsEditing(true);
        }
      } catch (error) {
        console.error('Erreur chargement réponses existantes:', error);
      }
    };

    loadExistingResponses();
  }, [patientData, taskId]);

  // Gérer le scroll intelligent des sections
  useEffect(() => {
    const handleScroll = () => {
      const container = document.querySelector('.questionnaire-content');
      if (!container) return;

      const scrollTop = container.scrollTop;
      const containerHeight = container.clientHeight;

      // Trouver la section active basée sur la position de scroll
      let newActiveSection = 0;
      sectionRefs.current.forEach((ref, index) => {
        if (ref) {
          const rect = ref.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          const relativeTop = rect.top - containerRect.top;

          if (relativeTop <= 100) { // Marge de 100px
            newActiveSection = index;
          }
        }
      });

      setActiveSection(newActiveSection);
    };

    const container = document.querySelector('.questionnaire-content');
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Questions du questionnaire PROMIS-33 en allemand
  const questions = {
    de: [
      {
        id: 'physical_function',
        title: 'Körperliche Funktionsfähigkeit',
        instruction: 'Bitte kreuzen Sie zu jeder Frage oder Aussage ein Kästchen an.',
        questions: [
          {
            id: 'PFA11',
            question: 'Können Sie Hausarbeiten erledigen, z.B. Staubsaugen oder Gartenarbeit?',
            options: [
              { value: 5, text: 'Ohne jede Schwierigkeiten' },
              { value: 4, text: 'Mit geringen Schwierigkeiten' },
              { value: 3, text: 'Mit einigen Schwierigkeiten' },
              { value: 2, text: 'Mit großen Schwierigkeiten' },
              { value: 1, text: 'Kann ich gar nicht' }
            ]
          },
          {
            id: 'PFA21',
            question: 'Können Sie mit normaler Geschwindigkeit Treppen hoch- und runtergehen?',
            options: [
              { value: 5, text: 'Ohne jede Schwierigkeiten' },
              { value: 4, text: 'Mit geringen Schwierigkeiten' },
              { value: 3, text: 'Mit einigen Schwierigkeiten' },
              { value: 2, text: 'Mit großen Schwierigkeiten' },
              { value: 1, text: 'Kann ich gar nicht' }
            ]
          },
          {
            id: 'PFA23',
            question: 'Können Sie mindestens 15 Minuten lang spazieren gehen?',
            options: [
              { value: 5, text: 'Ohne jede Schwierigkeiten' },
              { value: 4, text: 'Mit geringen Schwierigkeiten' },
              { value: 3, text: 'Mit einigen Schwierigkeiten' },
              { value: 2, text: 'Mit großen Schwierigkeiten' },
              { value: 1, text: 'Kann ich gar nicht' }
            ]
          },
          {
            id: 'PFA53',
            question: 'Können Sie Besorgungen machen und einkaufen gehen?',
            options: [
              { value: 5, text: 'Ohne jede Schwierigkeiten' },
              { value: 4, text: 'Mit geringen Schwierigkeiten' },
              { value: 3, text: 'Mit einigen Schwierigkeiten' },
              { value: 2, text: 'Mit großen Schwierigkeiten' },
              { value: 1, text: 'Kann ich gar nicht' }
            ]
          }
        ]
      },
      {
        id: 'anxiety',
        title: 'Emotionale Belastung - Angst',
        instruction: 'In den letzten 7 Tagen:',
        questions: [
          {
            id: 'EDANX01',
            question: 'Ich fürchtete mich.',
            options: [
              { value: 1, text: 'Nie' },
              { value: 2, text: 'Selten' },
              { value: 3, text: 'Manchmal' },
              { value: 4, text: 'Oft' },
              { value: 5, text: 'Immer' }
            ]
          },
          {
            id: 'EDANX40',
            question: 'Ich fand es schwer, auf etwas anderes zu achten als auf meine Angst.',
            options: [
              { value: 1, text: 'Nie' },
              { value: 2, text: 'Selten' },
              { value: 3, text: 'Manchmal' },
              { value: 4, text: 'Oft' },
              { value: 5, text: 'Immer' }
            ]
          },
          {
            id: 'EDANX41',
            question: 'Meine Sorgen haben mich überwältigt.',
            options: [
              { value: 1, text: 'Nie' },
              { value: 2, text: 'Selten' },
              { value: 3, text: 'Manchmal' },
              { value: 4, text: 'Oft' },
              { value: 5, text: 'Immer' }
            ]
          },
          {
            id: 'EDANX53',
            question: 'Ich fühlte mich unruhig.',
            options: [
              { value: 1, text: 'Nie' },
              { value: 2, text: 'Selten' },
              { value: 3, text: 'Manchmal' },
              { value: 4, text: 'Oft' },
              { value: 5, text: 'Immer' }
            ]
          }
        ]
      },
      {
        id: 'depression',
        title: 'Emotionale Belastung - Depressivität',
        instruction: 'In den letzten 7 Tagen:',
        questions: [
          {
            id: 'EDDEP04',
            question: 'Ich fühlte mich wertlos.',
            options: [
              { value: 1, text: 'Nie' },
              { value: 2, text: 'Selten' },
              { value: 3, text: 'Manchmal' },
              { value: 4, text: 'Oft' },
              { value: 5, text: 'Immer' }
            ]
          },
          {
            id: 'EDDEP06',
            question: 'Ich fühlte mich hilflos.',
            options: [
              { value: 1, text: 'Nie' },
              { value: 2, text: 'Selten' },
              { value: 3, text: 'Manchmal' },
              { value: 4, text: 'Oft' },
              { value: 5, text: 'Immer' }
            ]
          },
          {
            id: 'EDDEP29',
            question: 'Ich fühlte mich niedergeschlagen.',
            options: [
              { value: 1, text: 'Nie' },
              { value: 2, text: 'Selten' },
              { value: 3, text: 'Manchmal' },
              { value: 4, text: 'Oft' },
              { value: 5, text: 'Immer' }
            ]
          },
          {
            id: 'EDDEP41',
            question: 'Ich fühlte mich hoffnungslos.',
            options: [
              { value: 1, text: 'Nie' },
              { value: 2, text: 'Selten' },
              { value: 3, text: 'Manchmal' },
              { value: 4, text: 'Oft' },
              { value: 5, text: 'Immer' }
            ]
          }
        ]
      },
      {
        id: 'fatigue',
        title: 'Erschöpfung',
        instruction: 'In den letzten 7 Tagen:',
        questions: [
          {
            id: 'HI7',
            question: 'Ich bin erschöpft.',
            options: [
              { value: 1, text: 'Überhaupt nicht' },
              { value: 2, text: 'Ein wenig' },
              { value: 3, text: 'Mäßig' },
              { value: 4, text: 'Ziemlich' },
              { value: 5, text: 'Sehr' }
            ]
          },
          {
            id: 'AN3',
            question: 'Es fällt mir schwer, etwas anzufangen, weil ich müde bin.',
            options: [
              { value: 1, text: 'Überhaupt nicht' },
              { value: 2, text: 'Ein wenig' },
              { value: 3, text: 'Mäßig' },
              { value: 4, text: 'Ziemlich' },
              { value: 5, text: 'Sehr' }
            ]
          },
          {
            id: 'FATEXP41',
            question: 'Wie ausgelaugt fühlten Sie sich im Allgemeinen?',
            options: [
              { value: 1, text: 'Überhaupt nicht' },
              { value: 2, text: 'Ein wenig' },
              { value: 3, text: 'Mäßig' },
              { value: 4, text: 'Ziemlich' },
              { value: 5, text: 'Sehr' }
            ]
          },
          {
            id: 'FATEXP40',
            question: 'Wie erschöpft waren Sie im Allgemeinen?',
            options: [
              { value: 1, text: 'Überhaupt nicht' },
              { value: 2, text: 'Ein wenig' },
              { value: 3, text: 'Mäßig' },
              { value: 4, text: 'Ziemlich' },
              { value: 5, text: 'Sehr' }
            ]
          }
        ]
      },
      {
        id: 'sleep',
        title: 'Schlafbeeinträchtigung',
        instruction: 'In den letzten 7 Tagen:',
        questions: [
          {
            id: 'Sleep109',
            question: 'Meine Schlafqualität war ...',
            options: [
              { value: 5, text: 'Sehr gut' },
              { value: 4, text: 'Gut' },
              { value: 3, text: 'Mäßig' },
              { value: 2, text: 'Schlecht' },
              { value: 1, text: 'Sehr schlecht' }
            ]
          },
          {
            id: 'Sleep116',
            question: 'Mein Schlaf war erfrischend.',
            options: [
              { value: 5, text: 'Überhaupt nicht' },
              { value: 4, text: 'Ein wenig' },
              { value: 3, text: 'Mäßig' },
              { value: 2, text: 'Ziemlich' },
              { value: 1, text: 'Sehr' }
            ]
          },
          {
            id: 'Sleep20',
            question: 'Ich hatte Schlafprobleme.',
            options: [
              { value: 1, text: 'Überhaupt nicht' },
              { value: 2, text: 'Ein wenig' },
              { value: 3, text: 'Mäßig' },
              { value: 4, text: 'Ziemlich' },
              { value: 5, text: 'Sehr' }
            ]
          },
          {
            id: 'Sleep44',
            question: 'Ich hatte Schwierigkeiten einzuschlafen.',
            options: [
              { value: 1, text: 'Überhaupt nicht' },
              { value: 2, text: 'Ein wenig' },
              { value: 3, text: 'Mäßig' },
              { value: 4, text: 'Ziemlich' },
              { value: 5, text: 'Sehr' }
            ]
          }
        ]
      },
      {
        id: 'social',
        title: 'Teilhabe an sozialen Rollen und Aktivitäten',
        instruction: '',
        questions: [
          {
            id: 'SRPPER11-CaPS',
            question: 'Es fällt mir schwer, allen meinen regelmäßigen Freizeitaktivitäten mit anderen nachzugehen.',
            options: [
              { value: 5, text: 'Nie' },
              { value: 4, text: 'Selten' },
              { value: 3, text: 'Manchmal' },
              { value: 2, text: 'Oft' },
              { value: 1, text: 'Immer' }
            ]
          },
          {
            id: 'SRPPER18-CaPS',
            question: 'Es fällt mir schwer, allen Aktivitäten nachzugehen, die ich mit meiner Familie machen möchte.',
            options: [
              { value: 5, text: 'Nie' },
              { value: 4, text: 'Selten' },
              { value: 3, text: 'Manchmal' },
              { value: 2, text: 'Oft' },
              { value: 1, text: 'Immer' }
            ]
          },
          {
            id: 'SRPPER23-CaPS',
            question: 'Es fällt mir schwer, meine gewohnte Arbeit zu erledigen (einschließlich der Arbeit zu Hause).',
            options: [
              { value: 5, text: 'Nie' },
              { value: 4, text: 'Selten' },
              { value: 3, text: 'Manchmal' },
              { value: 2, text: 'Oft' },
              { value: 1, text: 'Immer' }
            ]
          },
          {
            id: 'SRPPER46-CaPS',
            question: 'Es fällt mir schwer, allen Aktivitäten nachzugehen, die ich mit Freunden machen möchte.',
            options: [
              { value: 5, text: 'Nie' },
              { value: 4, text: 'Selten' },
              { value: 3, text: 'Manchmal' },
              { value: 2, text: 'Oft' },
              { value: 1, text: 'Immer' }
            ]
          }
        ]
      },
      {
        id: 'pain_interference',
        title: 'Beeinträchtigung durch Schmerzen',
        instruction: 'In den letzten 7 Tagen:',
        questions: [
          {
            id: 'PAININ9',
            question: 'Wie sehr beeinträchtigten Schmerzen Ihre Alltagsaktivitäten?',
            options: [
              { value: 1, text: 'Überhaupt nicht' },
              { value: 2, text: 'Ein wenig' },
              { value: 3, text: 'Mäßig' },
              { value: 4, text: 'Ziemlich' },
              { value: 5, text: 'Sehr' }
            ]
          },
          {
            id: 'PAININ22',
            question: 'Wie sehr beeinträchtigten Schmerzen Ihre häuslichen Tätigkeiten?',
            options: [
              { value: 1, text: 'Überhaupt nicht' },
              { value: 2, text: 'Ein wenig' },
              { value: 3, text: 'Mäßig' },
              { value: 4, text: 'Ziemlich' },
              { value: 5, text: 'Sehr' }
            ]
          },
          {
            id: 'PAININ31',
            question: 'Wie sehr beeinträchtigten Schmerzen Ihre Fähigkeit, an sozialen Aktivitäten teilzunehmen?',
            options: [
              { value: 1, text: 'Überhaupt nicht' },
              { value: 2, text: 'Ein wenig' },
              { value: 3, text: 'Mäßig' },
              { value: 4, text: 'Ziemlich' },
              { value: 5, text: 'Sehr' }
            ]
          },
          {
            id: 'PAININ34',
            question: 'Wie sehr beeinträchtigten Schmerzen Sie bei Ihrer Hausarbeit?',
            options: [
              { value: 1, text: 'Überhaupt nicht' },
              { value: 2, text: 'Ein wenig' },
              { value: 3, text: 'Mäßig' },
              { value: 4, text: 'Ziemlich' },
              { value: 5, text: 'Sehr' }
            ]
          }
        ]
      },
      {
        id: 'pain_intensity',
        title: 'Schmerzintensität',
        instruction: 'In den letzten 7 Tagen:',
        questions: [
          {
            id: 'Global07',
            question: 'Wie würden Sie Ihre Schmerzen im Allgemeinen einschätzen?',
            type: 'pain_scale',
            minValue: 0,
            maxValue: 10,
            leftLabel: 'Keine Schmerzen',
            rightLabel: 'Schlimmste vorstellbare Schmerzen'
          }
        ]
      },
      {
        id: 'cognitive',
        title: 'Kognitive Funktionen',
        instruction: 'In den letzten 7 Tagen:',
        questions: [
          {
            id: 'PC2r',
            question: 'Ich war langsam beim Denken',
            options: [
              { value: 5, text: 'Nie' },
              { value: 4, text: 'Selten (einmal)' },
              { value: 3, text: 'Manchmal (zwei- oder dreimal)' },
              { value: 2, text: 'Oft (ungefähr einmal täglich)' },
              { value: 1, text: 'Sehr oft (mehrmals täglich)' }
            ]
          },
          {
            id: 'PC35r',
            question: 'Es kam mir so vor, als ob mein Gehirn nicht so gut wie sonst funktionierte',
            options: [
              { value: 5, text: 'Nie' },
              { value: 4, text: 'Selten (einmal)' },
              { value: 3, text: 'Manchmal (zwei- oder dreimal)' },
              { value: 2, text: 'Oft (ungefähr einmal täglich)' },
              { value: 1, text: 'Sehr oft (mehrmals täglich)' }
            ]
          },
          {
            id: 'PC36r',
            question: 'Ich musste mir größere Mühe geben als sonst, um den Überblick über das zu bewahren, womit ich gerade beschäftigt war',
            options: [
              { value: 5, text: 'Nie' },
              { value: 4, text: 'Selten (einmal)' },
              { value: 3, text: 'Manchmal (zwei- oder dreimal)' },
              { value: 2, text: 'Oft (ungefähr einmal täglich)' },
              { value: 1, text: 'Sehr oft (mehrmals täglich)' }
            ]
          },
          {
            id: 'PC42r',
            question: 'Ich hatte Schwierigkeiten, zwischen verschiedenen Aktivitäten, die ein gewisses Nachdenken erfordern, hin und her zu wechseln.',
            options: [
              { value: 5, text: 'Nie' },
              { value: 4, text: 'Selten (einmal)' },
              { value: 3, text: 'Manchmal (zwei- oder dreimal)' },
              { value: 2, text: 'Oft (ungefähr einmal täglich)' },
              { value: 1, text: 'Sehr oft (mehrmals täglich)' }
            ]
          }
        ]
      }
    ],
    en: [
      // Version anglaise à implémenter si nécessaire
    ]
  };

  const handleResponseChange = (questionId, value) => {
    setResponses(prev => {
      const existingResponse = prev[questionId];
      
      // Si la réponse existe et a la même valeur, garder l'ancien timestamp
      if (existingResponse && existingResponse.value === value) {
        return prev;
      }
      
      // Si c'est une nouvelle réponse ou une modification, utiliser le nouveau timestamp local
      return {
        ...prev,
        [questionId]: { 
          value, 
          timestamp: createLocalTimestamp()
        }
      };
    });
  };

  const calculateCompletionRate = () => {
    const allQuestions = questions[language].flatMap(section => 
      section.questions.map(q => q.id)
    );
    
    if (allQuestions.length === 0) return 0;
    
    const answeredQuestions = allQuestions.filter(questionId => 
      responses[questionId] && responses[questionId].value !== undefined
    );
    
    return answeredQuestions.length / allQuestions.length;
  };

  const isComplete = () => {
    return calculateCompletionRate() === 1;
  };

  const handleSubmit = async () => {
    if (!isComplete()) return;

    setLoading(true);
    setError(null);

    try {
      // Calculer un score basé sur les réponses (pour l'instant, on peut utiliser le taux de completion)
      const score = Math.round(calculateCompletionRate() * 100);
      
      const responseData = {
        patientId: patientData.patientId,
        taskId: taskId,
        questionnaireType: 'PROMIS-33',
        responses: JSON.stringify(responses),
        score: score,
        language: language,
        submissionDate: createLocalTimestamp()
      };

      console.log('Soumission PROMIS-33 avec données:', responseData);
      
      const response = await apiService.submitQuestionnaire(responseData);

      if (response.success) {
        onComplete && onComplete(responses);
      } else {
        setError(response.message || 'Erreur lors de la soumission');
      }
    } catch (error) {
      console.error('Erreur soumission questionnaire:', error);
      setError('Erreur lors de la soumission du questionnaire');
    } finally {
      setLoading(false);
    }
  };

  const currentQuestions = questions[language] || questions.de;

  return (
    <div className="questionnaire-container">
      <div className="questionnaire-content">
        <div className="questionnaire-header">
          <div className="questionnaire-title">PROMIS Profile 33</div>
          <button className="questionnaire-close" onClick={onClose}>×</button>
        </div>

        <div className="questionnaire-body">
          <div className="questionnaire-instructions">
            <p>Bitte kreuzen Sie zu jeder Frage oder Aussage ein Kästchen an.</p>
          </div>
          


          {currentQuestions.map((section, index) => (
            <div key={section.id} className="questions-list" ref={el => sectionRefs.current[index] = el}>
              <h3 className={`section-questionnaire ${activeSection === index ? 'active' : ''}`}>
                {section.title}
              </h3>
              {section.instruction && (
                <p className="section-instruction">{section.instruction}</p>
              )}
              
              {section.questions.map((question) => (
                <div key={question.id} className="question-item">
                  <h4 className="question-title">{question.question}</h4>
                  
                  {question.type === 'pain_scale' ? (
                    <div className="pain-scale-container">
                      <div className="pain-scale-labels">
                        <span className="pain-scale-left">{question.leftLabel}</span>
                        <span className="pain-scale-right">{question.rightLabel}</span>
                      </div>
                      <div className="pain-scale-input">
                        <input
                          type="range"
                          min={question.minValue}
                          max={question.maxValue}
                          value={responses[question.id] ? responses[question.id].value : 0}
                          onChange={(e) => handleResponseChange(question.id, parseInt(e.target.value))}
                          className="pain-scale-slider"
                          id={`slider-${question.id}`}
                        />
                        <div className="pain-scale-controls">
                          <button
                            type="button"
                            className="pain-scale-btn pain-scale-btn-minus"
                            onClick={() => {
                              const currentValue = responses[question.id] ? responses[question.id].value : 0;
                              const newValue = Math.max(question.minValue, currentValue - 1);
                              handleResponseChange(question.id, newValue);
                            }}
                            disabled={responses[question.id] ? responses[question.id].value <= question.minValue : true}

                          >
                            −
                          </button>
                          <input
                            type="number"
                            min={question.minValue}
                            max={question.maxValue}
                            value={responses[question.id] ? responses[question.id].value : 0}
                            onChange={(e) => {
                              const value = parseInt(e.target.value);
                              if (!isNaN(value) && value >= question.minValue && value <= question.maxValue) {
                                handleResponseChange(question.id, value);
                              }
                            }}
                            onBlur={(e) => {
                              const value = parseInt(e.target.value);
                              if (isNaN(value) || value < question.minValue || value > question.maxValue) {
                                // Remettre la valeur précédente si invalide
                                e.target.value = responses[question.id] ? responses[question.id].value : 0;
                              }
                            }}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.target.blur();
                              }
                            }}
                            className="pain-scale-value"
                            id={`input-${question.id}`}
                            placeholder="0"
                          />
                          <button
                            type="button"
                            className="pain-scale-btn pain-scale-btn-plus"
                            onClick={() => {
                              const currentValue = responses[question.id] ? responses[question.id].value : 0;
                              const newValue = Math.min(question.maxValue, currentValue + 1);
                              handleResponseChange(question.id, newValue);
                            }}
                            disabled={responses[question.id] ? responses[question.id].value >= question.maxValue : false}

                          >
                            +
                          </button>
                        </div>
                      </div>
                      <div className="pain-scale-ticks">
                        {Array.from({ length: question.maxValue - question.minValue + 1 }, (_, i) => (
                          <span key={i} className="pain-scale-tick">{i}</span>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="question-options">
                      {question.options.map((option) => (
                        <label key={option.value} className="option-label">
                          <input
                            type="radio"
                            name={question.id}
                            value={option.value}
                            checked={responses[question.id] && responses[question.id].value === option.value}
                            onChange={(e) => handleResponseChange(question.id, parseInt(e.target.value))}
                            className="option-input"
                          />
                          <span className="option-text">{option.text}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {/* Barre de progression des sections */}
        <div className="questionnaire-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${calculateCompletionRate() * 100}%` }}
            ></div>
          </div>
        </div>

        <div className="questionnaire-footer">
          <button
            className={`submit-btn ${!isComplete() || loading ? 'disabled' : ''}`}
            onClick={handleSubmit}
            disabled={!isComplete() || loading}
          >
            {loading 
              ? (language === 'de' ? 'Wird gesendet...' : 'Submitting...')
              : (language === 'de' ? 'Absenden' : 'Submit')
            }
          </button>
          

        </div>
      </div>
    </div>
  );
};

export default PROMIS33Questionnaire; 